import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { BadgeDollarSign, Download, CheckCircle2, Clock } from "lucide-react";

function toCsv(rows) {
  const header = ["contractor_number", "name", "email", "paid", "unpaid", "total"];
  const lines = [header.join(",")];
  for (const r of rows) {
    const total = (r.paid || 0) + (r.unpaid || 0);
    lines.push([
      r.contractor_number || "",
      `"${(r.name || "").replace(/"/g, '""')}"`,
      r.email || "",
      (r.paid || 0).toFixed(2),
      (r.unpaid || 0).toFixed(2),
      total.toFixed(2),
    ].join(","));
  }
  return lines.join("\n");
}

export default function Payouts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts/payouts/summary");
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = data ? Object.entries(data.by_contractor || {}).map(([id, v]) => ({ id, ...v })) : [];
  rows.sort((a, b) => (b.paid + b.unpaid) - (a.paid + a.unpaid));

  const exportCsv = () => {
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payouts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded.");
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="payouts-page">
      <Toaster position="top-right" richColors />
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><BadgeDollarSign className="w-3.5 h-3.5" /> Payouts · 1099-ready</div>
          <h1 className="text-3xl font-bold text-neutral-900">Contractor payouts</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl text-sm">
            Rolls up every contractor contract by payout status. Export as CSV when it's 1099 season.
          </p>
        </div>
        <button onClick={exportCsv} className="lf-btn-dark text-sm inline-flex items-center gap-2" data-testid="payouts-csv-btn">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <Kpi label="Paid" value={`$${(data?.totals?.paid || 0).toLocaleString()}`} accent="emerald" icon={CheckCircle2} />
        <Kpi label="Unpaid" value={`$${(data?.totals?.unpaid || 0).toLocaleString()}`} accent="amber" icon={Clock} />
        <Kpi label="Contractors" value={rows.length} />
      </div>

      {loading ? <div className="text-neutral-500 text-sm">Loading…</div> :
        rows.length === 0 ? (
          <div className="border border-dashed border-neutral-300 bg-white p-16 text-center text-neutral-500">
            No payouts recorded yet.
          </div>
        ) : (
          <div className="border border-neutral-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="payouts-table">
              <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-xs font-mono">
                <tr>
                  <th className="text-left px-4 py-3">CTR#</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-right px-4 py-3">Paid</th>
                  <th className="text-right px-4 py-3">Unpaid</th>
                  <th className="text-right px-4 py-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`payout-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{r.contractor_number || "—"}</td>
                    <td className="px-4 py-3 font-semibold">{r.name}</td>
                    <td className="px-4 py-3 text-neutral-500">{r.email || "—"}</td>
                    <td className="px-4 py-3 text-right text-emerald-700 font-semibold">${(r.paid || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-amber-700">${(r.unpaid || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-bold">${((r.paid || 0) + (r.unpaid || 0)).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
}

function Kpi({ label, value, accent, icon: Icon }) {
  const map = { emerald: "border-emerald-200 bg-emerald-50", amber: "border-amber-200 bg-amber-50" };
  return (
    <div className={`border p-4 ${map[accent] || "border-neutral-200 bg-white"}`}>
      <div className="overline flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />} {label}</div>
      <div className="text-2xl font-bold text-neutral-900 mt-1.5">{value ?? "—"}</div>
    </div>
  );
}
